import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../api/apiClient";
import { ContactInquiry, ContactResponse } from "../interfaces/Contact";

interface ContactState {
  loading: boolean;
  error: string | null;
  data: ContactResponse | null;
  exportData: ContactInquiry[] | null;
  pendingCount: number;
}

const initialState: ContactState = {
  loading: false,
  error: null,
  data: null,
  exportData: null,
  pendingCount: 0,
};

// Fetch contact stats (Admin count of pending inquiries)
export const fetchContactStats = createAsyncThunk(
  "contact/fetchStats",
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get("/api/v1/contact/stats");
      return response.data.data as { pendingCount: number; total: number };
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch contact stats";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Fetch paginated contact requests (Admin)
export const fetchContactInquiries = createAsyncThunk(
  "contact/fetchInquiries",
  async (
    params: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    } = {},
    thunkAPI,
  ) => {
    try {
      const { page = 1, limit = 10, search = "", status = "" } = params;
      const response = await apiClient.get("/api/v1/contact", {
        params: { page, limit, search, status },
      });
      return response.data.data as ContactResponse;
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch contact inquiries";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Toggle resolve status (Admin)
export const toggleResolveContact = createAsyncThunk(
  "contact/toggleResolve",
  async (
    { id, isResolved }: { id: string; isResolved?: boolean },
    thunkAPI,
  ) => {
    try {
      const response = await apiClient.patch(`/api/v1/contact/${id}/resolve`, {
        isResolved,
      });
      return response.data.data as ContactInquiry;
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to update contact status";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Export all contacts (Admin)
export const exportAllContactInquiries = createAsyncThunk(
  "contact/exportAll",
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get("/api/v1/contact/export");
      return response.data.data.contacts as ContactInquiry[];
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to export contact inquiries";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Delete contact (Admin)
export const deleteContactInquiry = createAsyncThunk(
  "contact/deleteInquiry",
  async (id: string, thunkAPI) => {
    try {
      await apiClient.delete(`/api/v1/contact/${id}`);
      return id;
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete contact inquiry";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const contactSlice = createSlice({
  name: "contact",
  initialState,
  reducers: {
    resetContactState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch Stats
      .addCase(fetchContactStats.fulfilled, (state, action) => {
        state.pendingCount = action.payload.pendingCount;
      })
      // Fetch Inquiries
      .addCase(fetchContactInquiries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContactInquiries.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.pendingCount = action.payload.pendingCount;
      })
      .addCase(fetchContactInquiries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Toggle Resolve
      .addCase(toggleResolveContact.fulfilled, (state, action) => {
        if (state.data?.contacts) {
          const index = state.data.contacts.findIndex(
            (c) => c.id === action.payload.id,
          );
          if (index !== -1) {
            const wasResolved = state.data.contacts[index].isResolved;
            state.data.contacts[index] = action.payload;

            if (wasResolved !== action.payload.isResolved) {
              if (action.payload.isResolved) {
                state.data.resolvedCount += 1;
                state.data.pendingCount = Math.max(
                  0,
                  state.data.pendingCount - 1,
                );
                state.pendingCount = state.data.pendingCount;
              } else {
                state.data.resolvedCount = Math.max(
                  0,
                  state.data.resolvedCount - 1,
                );
                state.data.pendingCount += 1;
                state.pendingCount = state.data.pendingCount;
              }
            }
          }
        }
      })
      // Delete Inquiry
      .addCase(deleteContactInquiry.fulfilled, (state, action) => {
        if (state.data?.contacts) {
          const removed = state.data.contacts.find(
            (c) => c.id === action.payload,
          );
          if (removed) {
            if (removed.isResolved) {
              state.data.resolvedCount = Math.max(
                0,
                state.data.resolvedCount - 1,
              );
            } else {
              state.data.pendingCount = Math.max(
                0,
                state.data.pendingCount - 1,
              );
              state.pendingCount = state.data.pendingCount;
            }
          }
          state.data.contacts = state.data.contacts.filter(
            (c) => c.id !== action.payload,
          );
          state.data.total = Math.max(0, state.data.total - 1);
        }
      });
  },
});

export const { resetContactState } = contactSlice.actions;
export default contactSlice.reducer;
